import { ChartMapper, JsonChartData } from 'adapter/out/client/widgets/data/mappers/ChartMapper';
import {
    asImageWidget,
    asTextWidget,
    JsonWidgetData,
    JsonWidgetDataError,
    JsonWidgetImageData,
    JsonWidgetTextData,
} from 'adapter/out/client/widgets/data/mappers/common';
import { asGaugeWidget, JsonWidgetGaugeData } from 'adapter/out/client/widgets/data/mappers/GaugeMapper';
import { asHeatmapData, JsonHeatmapData } from 'adapter/out/client/widgets/data/mappers/HeatmapMapper';
import { asPieData, JsonPieData } from 'adapter/out/client/widgets/data/mappers/PieMapper';
import { JsonSingleKPIData, singleKPIMapper } from 'adapter/out/client/widgets/data/mappers/singleKPIMapper';
import { asTableWidget, JsonTable } from 'adapter/out/client/widgets/data/mappers/TableMapper';
import { WidgetType } from 'domain/entity/widget';
import { WidgetDataTypes } from 'domain/entity/widgetData';
import { httpClientFunction } from 'domain/types';
import { left, right } from 'fp-ts/Either';
import ReadWidgetDataClient from 'use-case/widgets/data/read/readWidgetDataClient';
import ReadWidgetDataCriteria from 'use-case/widgets/data/read/readWidgetDataCriteria';

export default (httpClient: httpClientFunction, baseUrl: string): ReadWidgetDataClient => (criteria: ReadWidgetDataCriteria) =>
    httpClient(`${baseUrl}/dashboards/widgets/${criteria.widgetId}/data`, {})
        .then(async (response) =>
            response.ok
                ? response
                      .json()
                      .then(asWidgetData)
                      .then(right)
                      .catch((error: Error) => asWidgetDataError(error, 500))
                : asWidgetDataError(await response.json(), response.status)
        )
        .catch((error: Error) => asWidgetDataError(error, 500));

export const asWidgetDataError = (json: JsonWidgetDataError, status: number) => left({ error: new Error(json.message), status });

export const asWidgetData = (json: JsonWidgetData): WidgetDataTypes => {
    switch (json.type) {
        case WidgetType.SINGLE_KPI:
            return singleKPIMapper(json as JsonSingleKPIData);
        case WidgetType.LINE:
        case WidgetType.BAR:
        case WidgetType.STACKED_BAR:
            return ChartMapper(json as JsonChartData);
        case WidgetType.PIE:
            return asPieData(json as JsonPieData);
        case WidgetType.HEATMAP:
            return asHeatmapData(json as JsonHeatmapData);
        case WidgetType.TEXT:
            return asTextWidget((json as unknown) as JsonWidgetTextData);
        case WidgetType.IMAGE:
            return asImageWidget((json as unknown) as JsonWidgetImageData);
        case WidgetType.GAUGE:
            return asGaugeWidget(json as JsonWidgetGaugeData);
        case WidgetType.TABLE:
            return asTableWidget(json as JsonTable);
        default:
            throw new Error(`Missing AsWidgetData WidgetType Mapper implementation for ${json.type}`);
    }
};
